/**
 * RoofVerify renderer · iter303ai-fix9
 *
 * Verify-only step for Appraisal Booked · Schedule Appraisal · Action 8b.
 * Shows the CURRENT roof_screenshot_photo_id + roof_template_id set on the
 * property (captured in Actions 4 + 5 of this task). Operators can accept
 * with a single click OR "Change" — which re-opens the picker components
 * inline so they don't have to leave the runner.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ImageIcon, Layers, CheckCircle2, RefreshCw } from 'lucide-react';
import RoofDiagramPicker from '@/components/RoofDiagramPicker';
import RoofDiagramPickerInline from '@/components/RoofDiagramPickerInline';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function RoofVerify({ action, capturedData, onNext, onBack, onCancel, canBack }) {
  const propertyId = capturedData?.property_id;
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [template, setTemplate] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!propertyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}`);
      setProp(data);
      // iter303ai-fix13 · Load the actual photo record so we can render a
      // proper thumbnail (was showing only the ID). Falls back to nothing
      // if the record isn't found.
      const ssId = data.roof_screenshot_photo_id || data.roof_diagram_photo_id || '';
      if (ssId) {
        try {
          const p = await api.get(`/photos/by-id/${ssId}`);
          setPhoto(p.data);
        } catch { setPhoto(null); }
      } else {
        setPhoto(null);
      }
      if (data.roof_template_id) {
        try {
          const t = await api.get(`/roof-templates/${data.roof_template_id}`);
          setTemplate(t.data);
        } catch { setTemplate(null); }
      } else {
        setTemplate(null);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const screenshotId = prop?.roof_screenshot_photo_id || prop?.roof_diagram_photo_id || '';
  const templateId = prop?.roof_template_id || '';
  const isVerified = !!screenshotId && !!templateId;

  const setScreenshot = async (photoId) => {
    setSaving(true);
    try {
      await api.patch(`/properties/${propertyId}`, { roof_screenshot_photo_id: photoId });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (!propertyId) {
    return (
      <div className="p-6 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
        No property linked to this run — cannot verify roof.
        {canBack && <button onClick={onBack} className="ml-3 underline">← Back</button>}
      </div>
    );
  }

  return (
    <div data-testid="wf-roof-verify">
      <div className="bg-gradient-to-br from-indigo-50 to-white border-l-4 border-indigo-400 rounded-r-lg p-5 mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-indigo-600 font-semibold mb-1">
          <Layers size={12} /> Verify Roof Screenshot &amp; Template
        </div>
        <div className="text-slate-700 text-sm">
          Check the roof screenshot and main template captured earlier. If they look
          right, click Continue. If not, click Change to swap either one out.
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-slate-400 italic">Loading property…</div>
      ) : (
        <div className="mb-5 grid grid-cols-2 gap-4">
          {/* Screenshot preview card */}
          <div className={`border-2 rounded-lg p-4 ${screenshotId ? 'border-emerald-200 bg-emerald-50/30' : 'border-rose-200 bg-rose-50/30'}`}
            data-testid="wf-roof-verify-screenshot-card">
            <div className="flex items-center gap-2 mb-3">
              <ImageIcon size={14} className={screenshotId ? 'text-emerald-600' : 'text-rose-500'} />
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                Roof Screenshot
              </div>
              {screenshotId && <CheckCircle2 size={14} className="ml-auto text-emerald-500" />}
            </div>
            {screenshotId ? (
              <div className="flex gap-3 items-start">
                {photo?.url ? (
                  <img
                    src={photo.url}
                    alt="Roof screenshot"
                    data-testid="wf-roof-verify-screenshot-thumb"
                    className="w-32 h-24 object-cover rounded border border-slate-200 flex-shrink-0"
                  />
                ) : (
                  <div className="w-32 h-24 bg-slate-100 rounded border border-slate-200 flex items-center justify-center flex-shrink-0 text-[10px] text-slate-400 italic">
                    Preview unavailable
                  </div>
                )}
                <div className="text-[11px] text-slate-500 min-w-0">
                  <div className="font-semibold text-slate-700 truncate mb-1">
                    {photo?.original_name || photo?.description || 'Screenshot'}
                  </div>
                  <div className="font-mono text-[10px] break-all">
                    ID: {screenshotId.slice(0, 12)}…
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-rose-600 italic">Missing — needs a Google-Earth screenshot.</div>
            )}
          </div>

          {/* Template preview card */}
          <div className={`border-2 rounded-lg p-4 ${templateId ? 'border-emerald-200 bg-emerald-50/30' : 'border-rose-200 bg-rose-50/30'}`}
            data-testid="wf-roof-verify-template-card">
            <div className="flex items-center gap-2 mb-3">
              <Layers size={14} className={templateId ? 'text-emerald-600' : 'text-rose-500'} />
              <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500">
                Main Roof Template
              </div>
              {templateId && <CheckCircle2 size={14} className="ml-auto text-emerald-500" />}
            </div>
            {templateId ? (
              <div className="flex gap-3 items-start">
                {(template?.thumbnail_url || templateId) ? (
                  <img
                    src={template?.thumbnail_url || `/api/roof-templates/${templateId}/image`}
                    alt={template?.name || 'Roof template'}
                    data-testid="wf-roof-verify-template-thumb"
                    className="w-32 h-24 object-contain bg-white rounded border border-slate-200 flex-shrink-0"
                  />
                ) : (
                  <div className="w-32 h-24 bg-slate-100 rounded border border-slate-200 flex items-center justify-center flex-shrink-0 text-[10px] text-slate-400 italic">
                    Preview unavailable
                  </div>
                )}
                <div className="text-[11px] text-slate-500 min-w-0">
                  <div className="text-sm font-semibold text-slate-700 mb-1 truncate">
                    {template?.name || `(template ${templateId.slice(0, 8)}…)`}
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {template?.hip_count > 0    && <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-semibold">{template.hip_count} Hip</span>}
                    {template?.ridge_count > 0  && <span className="px-1.5 py-0.5 bg-orange-50 text-orange-700 rounded text-[10px] font-semibold">{template.ridge_count} Ridge</span>}
                    {template?.valley_count > 0 && <span className="px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded text-[10px] font-semibold">{template.valley_count} Valley</span>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-rose-600 italic">Missing — needs a template picked or traced.</div>
            )}
          </div>
        </div>
      )}

      {changing && (
        <div className="mb-5 border-2 border-indigo-200 rounded-lg bg-indigo-50/40 p-4 space-y-4"
          data-testid="wf-roof-verify-change-panel">
          <div className="text-[11px] uppercase tracking-widest text-indigo-700 font-semibold">
            Change Roof Screenshot
          </div>
          <RoofDiagramPicker
            api={api}
            propertyId={propertyId}
            selectedId={screenshotId}
            onSelect={setScreenshot}
            onClose={() => setChanging(false)}
          />
          <div className="border-t border-indigo-200 pt-4">
            <div className="text-[11px] uppercase tracking-widest text-indigo-700 font-semibold mb-2">
              Change Roof Template
            </div>
            <RoofDiagramPickerInline api={api} propertyId={propertyId} onChanged={load} />
          </div>
          {saving && <div className="text-xs text-slate-500 italic">Saving…</div>}
        </div>
      )}

      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        <button
          onClick={() => setChanging(c => !c)}
          data-testid="wf-roof-verify-change-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-indigo-500 text-indigo-700 font-semibold rounded hover:bg-indigo-50">
          <RefreshCw size={14} /> {changing ? 'Close change panel' : 'Change…'}
        </button>
        <button
          onClick={onNext}
          disabled={!isVerified || saving}
          data-testid="wf-roof-verify-continue-btn"
          className="px-6 py-2 bg-indigo-600 text-white font-semibold rounded hover:bg-indigo-700 disabled:opacity-40">
          {isVerified ? 'Looks right — Continue →' : 'Set screenshot + template first'}
        </button>
      </div>
    </div>
  );
}
